/**
 * Mapping from the legacy .html filenames used in the design source to
 * the routes in this React app. Lets us reuse content blocks that
 * reference the design's hrefs without rewriting every link.
 */
export const ROUTES = {
  home: "/",
  about: "/about",
  academics: "/academics",
  admissions: "/admissions",
  alumni: "/alumni",
  campusLife: "/campus-life",
  clubs: "/clubs",
  compliance: "/compliance",
  contact: "/contact",
  department: "/department",
  disclosures: "/disclosures",
  events: "/events",
  examinations: "/examinations",
  gallery: "/gallery",
  faculty: "/faculty",
  researchCenter: "/research/centers",
  hostels: "/hostels",
  international: "/international",
  library: "/library",
  mediaKit: "/media-kit",
  news: "/news",
  notifications: "/notifications",
  placements: "/placements",
  recruiters: "/recruiters",
  research: "/research",
  scholarships: "/scholarships",
  sustainability: "/sustainability",
  intlPartners: "/international/partners",
  committees: "/compliance/committees",
  hostelDetail: "/hostels",
  serviceWing: "/campus-life/service",
  leadership: "/about/leadership",
  visitors: "/about/visitors",
  deptCourses: "/department/courses",
  labDetail: "/labs",
  academicCalendar: "/academics/calendar",
  curriculum: "/academics/curriculum",
  reportConcern: "/compliance/report",
} as const;

export type RouteKey = keyof typeof ROUTES;

/** Translate a legacy ".html" href into the SPA route, leaving "#…" anchors alone. */
export function toRoute(href: string): string {
  if (!href) return href;
  if (href.startsWith("#") || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return href;
  }
  const map: Record<string, string> = {
    "index.html": ROUTES.home,
    "about.html": ROUTES.about,
    "academics.html": ROUTES.academics,
    "admissions.html": ROUTES.admissions,
    "alumni.html": ROUTES.alumni,
    "campus-life.html": ROUTES.campusLife,
    "clubs.html": ROUTES.clubs,
    "compliance.html": ROUTES.compliance,
    "contact.html": ROUTES.contact,
    "department.html": ROUTES.department,
    "disclosures.html": ROUTES.disclosures,
    "examinations.html": ROUTES.examinations,
    "faculty.html": ROUTES.faculty,
    "hostels.html": ROUTES.hostels,
    "international.html": ROUTES.international,
    "library.html": ROUTES.library,
    "media-kit.html": ROUTES.mediaKit,
    "news.html": ROUTES.news,
    "placements.html": ROUTES.placements,
    "recruiters.html": ROUTES.recruiters,
    "research.html": ROUTES.research,
    "scholarships.html": ROUTES.scholarships,
    "sustainability.html": ROUTES.sustainability,
  };
  // Drop hash fragments while resolving the file part
  const [file, hash] = href.split("#");
  const route = map[file] ?? href;
  return hash ? `${route}#${hash}` : route;
}
